import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { authColors } from '../../styles/colors';
import PublicHeader from '../../components/layout/PublicHeader';
import PublicFooter from '../../components/layout/PublicFooter';
import SEO from '../../components/seo/SEO';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { useUmamiSectionViewRef, useLandingScroll, useHeroEngaged, useHeroVideoUmamiHandlers } from '../../hooks/useUmamiSectionView';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { trackUmamiEvent } from '../../utils/umami';
import { clearLoggingOutFlag } from '../../utils/authReset';
import {
  Users,
  FolderKanban,
  Calendar,
  MessageSquare,
  DollarSign,
  BarChart3,
  CheckCircle2,
  ArrowRight,
  Zap,
  ChevronDown,
  AlertTriangle,
  type LucideIcon,
} from 'lucide-react';

const HERO_REVEAL_OPTIONS = { threshold: 0.1, rootMargin: '0px' } as const;
/** Trigger section reveals before they enter the viewport so scroll never feels empty. */
const SECTION_REVEAL_OPTIONS = { threshold: 0, rootMargin: '0px 0px 150px 0px' } as const;

const MOTION_SAFE =
  'motion-reduce:opacity-100 motion-reduce:translate-y-0 motion-reduce:transition-none';

const heroReveal = (visible: boolean) =>
  visible ? `opacity-100 translate-y-0 ${MOTION_SAFE}` : `opacity-0 translate-y-8 ${MOTION_SAFE}`;

const sectionReveal = (visible: boolean) =>
  visible ? `opacity-100 translate-y-0 ${MOTION_SAFE}` : `opacity-0 translate-y-3 ${MOTION_SAFE}`;

const HERO_TRANSITION = `transition-all duration-700 ease-out ${MOTION_SAFE}`;
const SECTION_TRANSITION = `transition-all duration-400 ease-out ${MOTION_SAFE}`;

const SCREENSHOT_FALLBACK =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5TY3JlZW5zaG90PC90ZXh0Pjwvc3ZnPg==';

const FAQ_KEYS = ['faq1', 'faq2', 'faq3', 'faq4'] as const;
const PAIN_POINT_KEYS = ['painPoint1', 'painPoint2', 'painPoint3', 'painPoint4'] as const;

const SECTION_LABEL =
  'inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold uppercase tracking-wide bg-blue-50 text-blue-700 border border-blue-100';

const SCREENSHOT_IMAGE = 'w-full h-auto block rounded-xl shadow-lg shadow-slate-300/40 ring-1 ring-slate-200/80';

const HERO_VIDEO_POSTER = '/images/screenshots/dashboard.png';
const HERO_VIDEO_SRC = '/videos/leadsme_demo.mp4';
const HERO_VIDEO_CLASS =
  'w-full h-auto block rounded-xl bg-slate-900/5 shadow-xl shadow-slate-400/35 ring-1 ring-slate-300/80';

/** Shared public shell width — header, hero, footer, sections */
const PUBLIC_SHELL =
  'max-w-screen-2xl mx-auto w-full px-4 sm:px-6 lg:px-10 xl:px-12';

const CTA_BUTTON_CLASS = `w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 text-base lg:text-lg font-semibold rounded-xl ${authColors.primaryGradient} text-white shadow-lg hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-300 transform hover:scale-105 active:scale-95`;

const FEATURE_SECTION_CLASS =
  `grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-6 sm:gap-8 md:gap-10 items-center ${SECTION_TRANSITION}`;

type Observer = ReturnType<typeof useIntersectionObserver<HTMLDivElement>>;

const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const handleLearnMoreClick = () => {
  trackUmamiEvent('learn_more_click');
  scrollToSection('why-leadsme');
};

const handleSignupClick = (source: 'hero' | 'cta_bottom') => {
  trackUmamiEvent('signup_click', { source });
};

const LandingScreenshot: React.FC<{
  src: string;
  alt: string;
  loading?: 'eager' | 'lazy';
  className?: string;
}> = ({ src, alt, loading = 'lazy', className = SCREENSHOT_IMAGE }) => (
  <img
    src={src}
    alt={alt}
    className={className}
    loading={loading}
    onError={(e) => {
      e.currentTarget.src = SCREENSHOT_FALLBACK;
    }}
  />
);

const LandingHeroVideo: React.FC<{
  ariaLabel: string;
  videoClassName?: string;
  onPlay: () => void;
  onTimeUpdate: (event: React.SyntheticEvent<HTMLVideoElement>) => void;
}> = ({ ariaLabel, videoClassName = '', onPlay, onTimeUpdate }) => (
  <div className="relative">
    <div
      className="absolute -inset-3 sm:-inset-4 bg-gradient-to-r from-blue-400/15 to-cyan-400/15 rounded-3xl blur-2xl -z-10 pointer-events-none"
      aria-hidden
    />
    <video
      className={`${HERO_VIDEO_CLASS} ${videoClassName}`}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      poster={HERO_VIDEO_POSTER}
      aria-label={ariaLabel}
      onPlay={onPlay}
      onTimeUpdate={onTimeUpdate}
    >
      <source src={HERO_VIDEO_SRC} type="video/mp4" />
    </video>
  </div>
);

const BenefitList: React.FC<{ keys: readonly string[] }> = ({ keys }) => {
  const { t } = useI18n();

  return (
    <ul className="space-y-2 sm:space-y-3">
      {keys.map((key) => (
        <li key={key} className="flex items-start gap-2 sm:gap-3">
          <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
          <span className="text-sm sm:text-base text-slate-700">{t(`landing.${key}`)}</span>
        </li>
      ))}
    </ul>
  );
};

const GOOGLE_COMPAT_ITEMS = [
  { name: 'Gmail', src: '/images/brands/gmail.svg' },
  { name: 'Calendar', src: '/images/brands/google-calendar.svg' },
  { name: 'Drive', src: '/images/brands/google-drive.svg' },
] as const;

const GOOGLE_INTEGRATION_CARDS = [
  {
    nameKey: 'integrationsGmailName',
    descriptionKey: 'integrationsGmailDescription',
    src: '/images/brands/gmail.svg',
  },
  {
    nameKey: 'integrationsCalendarName',
    descriptionKey: 'integrationsCalendarDescription',
    src: '/images/brands/google-calendar.svg',
  },
  {
    nameKey: 'integrationsDriveName',
    descriptionKey: 'integrationsDriveDescription',
    src: '/images/brands/google-drive.svg',
  },
] as const;

/** Compact “Compatible with” strip under the hero CTA (official logos + names). */
const HeroGoogleCompat: React.FC = () => {
  const { t } = useI18n();

  return (
    <div
      className="inline-flex flex-wrap items-center justify-center 2xl:justify-start gap-x-3 gap-y-2 px-3 py-2 rounded-xl border border-slate-200/90 bg-white/90 shadow-sm backdrop-blur-sm"
      aria-label={t('landing.heroCompatLabel')}
    >
      <span className="text-xs sm:text-sm text-slate-500 font-medium">
        {t('landing.heroCompatLabel')}
      </span>
      <ul className="flex flex-wrap items-center gap-x-3 gap-y-1.5 list-none m-0 p-0">
        {GOOGLE_COMPAT_ITEMS.map((item) => (
          <li
            key={item.name}
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-slate-700"
          >
            <img
              src={item.src}
              alt=""
              width={16}
              height={16}
              className="w-4 h-4 flex-shrink-0 object-contain"
              loading="lazy"
              decoding="async"
            />
            <span>{item.name}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const FeatureSection: React.FC<{
  observer: Observer;
  icon: LucideIcon;
  screenshotSrc: string;
  labelKey: string;
  titleKey: string;
  descriptionKey: string;
  benefitKeys: readonly string[];
  sectionClassName?: string;
}> = ({
  observer,
  icon: Icon,
  screenshotSrc,
  labelKey,
  titleKey,
  descriptionKey,
  benefitKeys,
  sectionClassName = '',
}) => {
  const { t } = useI18n();

  return (
    <div
      ref={observer.elementRef}
      className={`${FEATURE_SECTION_CLASS} ${sectionReveal(observer.hasBeenVisible)} ${sectionClassName}`}
    >
      <div className="order-2 lg:order-1">
        <div className={`${SECTION_LABEL} mb-3 sm:mb-4`}>
          <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
          <span>{t(`landing.${labelKey}`)}</span>
        </div>
        <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-slate-900 mb-3 sm:mb-4">
          {t(`landing.${titleKey}`)}
        </h3>
        <p className="text-base sm:text-lg lg:text-xl text-slate-600 mb-4 sm:mb-6">
          {t(`landing.${descriptionKey}`)}
        </p>
        <BenefitList keys={benefitKeys} />
      </div>
      <div className="order-1 lg:order-2 relative">
        <LandingScreenshot src={screenshotSrc} alt={t(`landing.${titleKey}`)} />
      </div>
    </div>
  );
};

const FEATURES = [
  {
    id: 'dashboard',
    icon: BarChart3,
    screenshotSrc: '/images/screenshots/dashboard.png',
    labelKey: 'featureDashboardLabel',
    titleKey: 'featureDashboardTitle',
    descriptionKey: 'featureDashboardDescription',
    benefitKeys: ['dashboardBenefit1', 'dashboardBenefit2', 'dashboardBenefit3'],
    sectionClassName:
      'hidden lg:grid mb-16 sm:mb-20 md:mb-24 lg:mb-28 pb-16 sm:pb-20 border-b border-slate-100',
  },
  {
    id: 'leads',
    icon: Users,
    screenshotSrc: '/images/screenshots/leads.png',
    labelKey: 'featureLeadsLabel',
    titleKey: 'featureLeadsTitle',
    descriptionKey: 'featureLeadsDescription',
    benefitKeys: ['leadsBenefit1', 'leadsBenefit2', 'leadsBenefit3'],
    sectionClassName: 'mb-10 sm:mb-16 md:mb-20 lg:mb-28 pb-10 sm:pb-16 border-b border-slate-100',
  },
  {
    id: 'projects',
    icon: FolderKanban,
    screenshotSrc: '/images/screenshots/projects.png',
    labelKey: 'featureProjectsLabel',
    titleKey: 'featureProjectsTitle',
    descriptionKey: 'featureProjectsDescription',
    benefitKeys: ['projectsBenefit1', 'projectsBenefit2', 'projectsBenefit3'],
    sectionClassName: 'mb-10 sm:mb-16 md:mb-20 lg:mb-28 pb-10 sm:pb-16 border-b border-slate-100',
  },
  {
    id: 'calendar',
    icon: Calendar,
    screenshotSrc: '/images/screenshots/calendar.png',
    labelKey: 'featureCalendarLabel',
    titleKey: 'featureCalendarTitle',
    descriptionKey: 'featureCalendarDescription',
    benefitKeys: ['calendarBenefit1', 'calendarBenefit2', 'calendarBenefit3'],
    sectionClassName: 'mb-10 sm:mb-16 md:mb-20 lg:mb-28 pb-10 sm:pb-16 border-b border-slate-100',
  },
  {
    id: 'finances',
    icon: DollarSign,
    screenshotSrc: '/images/screenshots/finances.png',
    labelKey: 'featureFinancesLabel',
    titleKey: 'featureFinancesTitle',
    descriptionKey: 'featureFinancesDescription',
    benefitKeys: ['financesBenefit1', 'financesBenefit2', 'financesBenefit3'],
    sectionClassName: 'mb-10 sm:mb-16 md:mb-20 lg:mb-28 pb-10 sm:pb-16 border-b border-slate-100',
  },
  {
    id: 'messages',
    icon: MessageSquare,
    screenshotSrc: '/images/screenshots/messages.png',
    labelKey: 'featureMessagesLabel',
    titleKey: 'featureMessagesTitle',
    descriptionKey: 'featureMessagesDescription',
    benefitKeys: ['messagesBenefit1', 'messagesBenefit2', 'messagesBenefit3'],
    sectionClassName: '',
  },
] as const;

const LandingPage: React.FC = () => {
  const { t, tSeo } = useI18n();
  const heroScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    clearLoggingOutFlag();
  }, []);

  // Defensive: unlock body if a modal / Google Picker left overflow:hidden
  useEffect(() => {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.documentElement.style.overflow = '';
    document.querySelectorAll('.picker-dialog-bg, .picker-dialog').forEach((el) => el.remove());
  }, []);

  const heroBadgeRef = useIntersectionObserver(HERO_REVEAL_OPTIONS);
  const heroTitleRef = useIntersectionObserver(HERO_REVEAL_OPTIONS);
  const heroDescriptionRef = useIntersectionObserver(HERO_REVEAL_OPTIONS);
  const heroCtaRef = useIntersectionObserver(HERO_REVEAL_OPTIONS);
  const heroTrustRef = useIntersectionObserver(HERO_REVEAL_OPTIONS);
  const heroDashboardRef = useIntersectionObserver(HERO_REVEAL_OPTIONS);
  const painRef = useIntersectionObserver(SECTION_REVEAL_OPTIONS);
  const featuresRef = useIntersectionObserver(SECTION_REVEAL_OPTIONS);
  const featureObservers = {
    dashboard: useIntersectionObserver(SECTION_REVEAL_OPTIONS),
    leads: useIntersectionObserver(SECTION_REVEAL_OPTIONS),
    projects: useIntersectionObserver(SECTION_REVEAL_OPTIONS),
    calendar: useIntersectionObserver(SECTION_REVEAL_OPTIONS),
    finances: useIntersectionObserver(SECTION_REVEAL_OPTIONS),
    messages: useIntersectionObserver(SECTION_REVEAL_OPTIONS),
  };
  const faqRef = useIntersectionObserver(SECTION_REVEAL_OPTIONS);
  const integrationsRef = useIntersectionObserver(SECTION_REVEAL_OPTIONS);
  const ctaRef = useIntersectionObserver(SECTION_REVEAL_OPTIONS);

  const painUmamiRef = useUmamiSectionViewRef('pain');
  const featuresUmamiRef = useUmamiSectionViewRef('features');
  const integrationsUmamiRef = useUmamiSectionViewRef('integrations');
  const faqUmamiRef = useUmamiSectionViewRef('faq');
  const ctaUmamiRef = useUmamiSectionViewRef('cta');

  useLandingScroll(heroScrollRef);
  useHeroEngaged();
  const { onPlay: onHeroVideoPlay, onTimeUpdate: onHeroVideoTimeUpdate } = useHeroVideoUmamiHandlers();
  // One <video> only — CSS-hidden duplicate still downloads (autoplay). Match Tailwind `2xl`
  // so iPad Pro (incl. 12.9" landscape ~1366px) keeps stacked hero text + video.
  const isHeroDesktop = useMediaQuery('(min-width: 1536px)');

  const heroVideo = (
    <LandingHeroVideo
      ariaLabel={t('landing.heroDemoVideoLabel')}
      videoClassName={isHeroDesktop ? 'transform hover:scale-[1.01] transition-transform duration-500' : ''}
      onPlay={onHeroVideoPlay}
      onTimeUpdate={onHeroVideoTimeUpdate}
    />
  );

  return (
    <div className="min-h-screen flex flex-col bg-white overflow-x-hidden">
      <SEO
        title={tSeo('landing.seoTitle')}
        description={tSeo('landing.description')}
        canonical="https://leadsme.app/"
      />
      <PublicHeader />

      <div className="flex-1">
        {/* Hero — full first viewport; wide canvas for video */}
        <section className="relative min-h-svh flex flex-col bg-[#f8fafc] pt-16 sm:pt-20 lg:min-h-svh">
          <div className="absolute inset-0 -z-0 pointer-events-none" aria-hidden>
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_rgba(59,130,246,0.12),_transparent_50%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(6,182,212,0.1),_transparent_45%)]" />
            <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(to_bottom,white,transparent)]" />
          </div>
          <div
            ref={heroScrollRef}
            className="relative z-10 flex flex-col flex-1 min-h-0 overflow-x-hidden"
          >
            <div className={`${PUBLIC_SHELL} flex flex-col flex-1 min-h-0 justify-center pt-2 sm:pt-5 lg:pt-4 xl:pt-6 pb-1 sm:pb-4 lg:pb-2 xl:pb-3`}>
              <div
                ref={heroDashboardRef.elementRef}
                className="grid grid-cols-1 2xl:grid-cols-[minmax(0,1fr)_minmax(0,1.55fr)] gap-4 sm:gap-5 2xl:gap-10 items-center"
              >
                <div className="text-center 2xl:text-left flex flex-col">
                  <div
                    ref={heroBadgeRef.elementRef}
                    className={`inline-flex w-fit self-center 2xl:self-start items-center gap-2 px-3 py-1.5 sm:px-3.5 sm:py-1.5 bg-white/90 text-blue-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-5 2xl:mb-5 shadow-sm border border-blue-100/80 backdrop-blur-sm ${HERO_TRANSITION} ${heroReveal(heroBadgeRef.hasBeenVisible)}`}
                  >
                    <Zap className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>{t('landing.heroBadge')}</span>
                  </div>

                  <h1
                    ref={heroTitleRef.elementRef}
                    className={`text-[2rem] sm:text-5xl md:text-6xl xl:text-7xl font-bold text-slate-900 leading-[1.1] mb-2 sm:mb-5 2xl:mb-5 tracking-tight ${HERO_TRANSITION} ${heroReveal(heroTitleRef.hasBeenVisible)}`}
                    style={{ transitionDelay: '100ms' }}
                  >
                    {t('landing.heroTitle')}{' '}
                    <span className="bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-600 bg-clip-text text-transparent">
                      {t('landing.heroTitleHighlight')}
                    </span>
                  </h1>

                  <p
                    ref={heroDescriptionRef.elementRef}
                    className={`max-w-xl xl:max-w-2xl mx-auto 2xl:mx-0 text-lg sm:text-xl lg:text-2xl text-slate-600 leading-relaxed ${HERO_TRANSITION} ${heroReveal(heroDescriptionRef.hasBeenVisible)}`}
                    style={{ transitionDelay: '200ms' }}
                  >
                    {t('landing.heroDescription')}
                  </p>

                  <div
                    className={`2xl:hidden mt-4 w-full ${HERO_TRANSITION} relative ${heroReveal(heroDashboardRef.hasBeenVisible)}`}
                    style={{ transitionDelay: '250ms' }}
                  >
                    {!isHeroDesktop ? heroVideo : null}
                  </div>

                  <div
                    ref={heroCtaRef.elementRef}
                    className={`mt-4 sm:mt-5 2xl:mt-6 flex flex-col items-center 2xl:items-start gap-2.5 sm:gap-3 ${HERO_TRANSITION} ${heroReveal(heroCtaRef.hasBeenVisible)}`}
                    style={{ transitionDelay: '300ms' }}
                  >
                    <Link to="/signup" className={CTA_BUTTON_CLASS} onClick={() => handleSignupClick('hero')}>
                      {t('landing.ctaButton')}
                      <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                    </Link>
                    <HeroGoogleCompat />
                  </div>

                  <div
                    ref={heroTrustRef.elementRef}
                    className={`mt-2.5 sm:mt-3 2xl:mt-3 ${HERO_TRANSITION} ${heroReveal(heroTrustRef.hasBeenVisible)}`}
                    style={{ transitionDelay: '350ms' }}
                  >
                    <div className="flex justify-center 2xl:justify-start">
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 border border-slate-200/80 text-xs sm:text-sm text-slate-600 shadow-sm backdrop-blur-sm">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        {t('landing.trustSignup')}
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  className={`hidden 2xl:block ${HERO_TRANSITION} relative ${heroReveal(heroDashboardRef.hasBeenVisible)}`}
                  style={{ transitionDelay: '200ms' }}
                >
                  {isHeroDesktop ? heroVideo : null}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={handleLearnMoreClick}
              className="shrink-0 flex flex-col items-center justify-center gap-0.5 pt-0.5 pb-2 sm:pb-3 lg:pb-2 px-6 text-slate-500 [@media(hover:hover)]:hover:text-blue-600 active:text-blue-600 focus:text-slate-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30 focus-visible:ring-offset-2 rounded-lg transition-colors min-h-[40px] w-full"
              aria-label={t('landing.heroScrollCue')}
            >
              <span className="text-xs sm:text-sm font-medium">{t('landing.heroScrollCue')}</span>
              <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6 animate-bounce" aria-hidden />
            </button>
          </div>
        </section>

        {/* Why Leadsme / pain points */}
        <section
          id="why-leadsme"
          ref={painUmamiRef}
          className="relative py-12 sm:py-20 bg-slate-50 scroll-mt-16 border-y border-slate-100"
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-12">
            <div
              ref={painRef.elementRef}
              className={`${SECTION_TRANSITION} ${sectionReveal(painRef.hasBeenVisible)}`}
            >
              <div className="text-center mb-8 sm:mb-10 md:mb-12">
                <p className={`${SECTION_LABEL} mb-4 mx-auto w-fit`}>
                  {t('landing.painEyebrow')}
                </p>
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-3 sm:mb-4 tracking-tight">
                  {t('landing.whyTitle')}
                </h2>
                <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
                  {t('landing.whySubtitle')}
                </p>
              </div>

              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 mb-5 sm:mb-6">
                {PAIN_POINT_KEYS.map((key) => (
                  <li
                    key={key}
                    className="flex flex-row items-center gap-3 rounded-2xl border border-slate-200/80 bg-white p-5 sm:p-6 shadow-sm shadow-slate-200/40 text-left"
                  >
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-rose-50 flex-shrink-0">
                      <AlertTriangle className="w-5 h-5 text-rose-500" aria-hidden />
                    </span>
                    <span className="text-sm sm:text-base font-medium text-slate-800 leading-snug">
                      {t(`landing.${key}`)}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="flex items-center gap-3 sm:gap-4 rounded-2xl border border-blue-200 bg-blue-50/80 px-4 py-4 sm:px-5 sm:py-4">
                <span className="inline-flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-blue-600 flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-white" aria-hidden />
                </span>
                <p className="text-sm sm:text-base md:text-lg font-semibold text-slate-900 text-left leading-snug">
                  {t('landing.painSolutionPrefix')}{' '}
                  <span className="text-blue-600">{t('landing.painSolutionHighlight')}</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section id="features" ref={featuresUmamiRef} className="relative py-12 sm:py-20 md:py-24 bg-white scroll-mt-16">
          <div className={`${PUBLIC_SHELL}`}>
            <div
              ref={featuresRef.elementRef}
              className={`text-center mb-10 sm:mb-16 md:mb-20 ${SECTION_TRANSITION} ${sectionReveal(featuresRef.hasBeenVisible)}`}
            >
              <p className={`${SECTION_LABEL} mb-4 mx-auto w-fit`}>{t('landing.featuresEyebrow')}</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-3 sm:mb-4 px-1 tracking-tight">
                {t('landing.featuresTitle')}
              </h2>
            </div>

            {FEATURES.map((feature) => (
              <FeatureSection
                key={feature.id}
                observer={featureObservers[feature.id]}
                icon={feature.icon}
                screenshotSrc={feature.screenshotSrc}
                labelKey={feature.labelKey}
                titleKey={feature.titleKey}
                descriptionKey={feature.descriptionKey}
                benefitKeys={feature.benefitKeys}
                sectionClassName={feature.sectionClassName}
              />
            ))}
          </div>
        </section>

        {/* Google integrations — after features, before FAQ */}
        <section
          id="integrations"
          ref={integrationsUmamiRef}
          className="relative py-12 sm:py-20 md:py-24 bg-slate-50 border-t border-slate-100 scroll-mt-16"
        >
          <div className={`${PUBLIC_SHELL}`}>
            <div
              ref={integrationsRef.elementRef}
              className={`text-center max-w-3xl mx-auto mb-8 sm:mb-12 md:mb-14 ${SECTION_TRANSITION} ${sectionReveal(integrationsRef.hasBeenVisible)}`}
            >
              <p className={`${SECTION_LABEL} mb-4 mx-auto w-fit`}>
                {t('landing.integrationsEyebrow')}
              </p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-3 sm:mb-4 tracking-tight">
                {t('landing.integrationsTitle')}
              </h2>
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                {t('landing.integrationsDescription')}
              </p>
            </div>

            <div
              className={`grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 md:gap-6 max-w-5xl mx-auto ${SECTION_TRANSITION} ${sectionReveal(integrationsRef.hasBeenVisible)}`}
            >
              {GOOGLE_INTEGRATION_CARDS.map((card) => (
                <div
                  key={card.nameKey}
                  className="flex flex-col items-start text-left rounded-2xl border border-slate-200/80 bg-white p-5 sm:p-6 shadow-sm shadow-slate-200/40"
                >
                  <img
                    src={card.src}
                    alt=""
                    width={40}
                    height={40}
                    className="w-10 h-10 object-contain mb-4"
                    loading="lazy"
                    decoding="async"
                  />
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2">
                    {t(`landing.${card.nameKey}`)}
                  </h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                    {t(`landing.${card.descriptionKey}`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section ref={faqUmamiRef} className="relative py-12 sm:py-20 bg-white border-t border-slate-100">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-12">
            <div
              ref={faqRef.elementRef}
              className={`${SECTION_TRANSITION} ${sectionReveal(faqRef.hasBeenVisible)}`}
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 text-center mb-8 sm:mb-10 tracking-tight">
                {t('landing.faqTitle')}
              </h2>
              <div className="space-y-3">
                {FAQ_KEYS.map((key) => (
                  <details
                    key={key}
                    className="group rounded-xl border border-slate-200/80 bg-white shadow-sm open:border-blue-200 open:shadow-md open:shadow-blue-500/5 transition-all"
                    onToggle={(e) => {
                      if ((e.currentTarget as HTMLDetailsElement).open) {
                        trackUmamiEvent('faq_open', { question: key });
                      }
                    }}
                  >
                    <summary className="flex items-center justify-between gap-4 px-4 sm:px-5 py-4 cursor-pointer text-sm sm:text-base font-semibold text-slate-900 text-left list-none [&::-webkit-details-marker]:hidden">
                      <span className="flex-1 min-w-0">{t(`landing.${key}Question`)}</span>
                      <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0 transition-transform group-open:rotate-180" />
                    </summary>
                    <p className="px-4 sm:px-5 pb-4 text-sm sm:text-base text-slate-600 leading-relaxed text-left border-t border-slate-100 pt-3">
                      {t(`landing.${key}Answer`)}
                    </p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section ref={ctaUmamiRef} className="relative py-14 sm:py-20 bg-slate-50 border-t border-slate-100">
          <div
            ref={ctaRef.elementRef}
            className={`max-w-2xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-12 text-center ${SECTION_TRANSITION} ${sectionReveal(ctaRef.hasBeenVisible)}`}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 tracking-tight">
              {t('landing.ctaTitle')}
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-slate-600 mb-8 max-w-lg mx-auto leading-relaxed">
              {t('landing.ctaDescription')}
            </p>
            <div className="flex flex-col items-center gap-3">
              <Link to="/signup" className={CTA_BUTTON_CLASS} onClick={() => handleSignupClick('cta_bottom')}>
                {t('landing.ctaButton')}
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </Link>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200/80 text-xs sm:text-sm text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                {t('landing.trustSignup')}
              </span>
            </div>
          </div>
        </section>
      </div>

      <PublicFooter />
    </div>
  );
};

export default LandingPage;
